import { inventoryWorkloadsTableKeys } from '../consts';
import { WORKLOAD_CATEGORIES, type WorkloadCategory } from '../_constants';

// WOOP group (Autoscaling <-> Savings): list criteria, no cross-mapping.
// Autoscaling useWorkloadsTableCriteria and Savings useImpactWorkloadsCriteria
// share these keys with the same string[] value shape (namespace/managedBy/
// verticalOptimization/horizontalOptimization from QuickSearchFilter `multiple`
// or Autocomplete multi; workloadId/workloadName/kind from
// makeSearchBoxCriteria / QuickSearchFilter `multiple`). Autoscaling-only
// keys (verticalScalingPolicy, predictive, optimized, hpa, jvm, status,
// access) are NOT carried, because Savings has no such criteria.
const WOOP_SHARED_FILTER_KEYS = [
  'namespace',
  'workloadName',
  'workloadId',
  'kind',
  'managedBy',
  'verticalOptimization',
  'horizontalOptimization',
] as const;

// Reporting group (Compute/Network/Reliability/GPU): same
// useWorkloadsSearchCriteria hook for Compute/Network/Reliability; GPU uses
// makeWorkloadsGpuUtilizationSearchCriteria which has the same key set
// (workloadNames, workloadTypes, namespaces, labels).
const REPORTING_SHARED_FILTER_KEYS = [
  'namespaces',
  'workloadNames',
  'workloadTypes',
  'labels',
] as const;

// Keys that also carry across groups (WOOP key -> Reporting key).
const WOOP_TO_REPORTING_KEYS: Record<string, string> = {
  namespace: 'namespaces',
  workloadName: 'workloadNames',
  kind: 'workloadTypes',
};

const REPORTING_TO_WOOP_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(WOOP_TO_REPORTING_KEYS).map(([woop, reporting]) => [
    reporting,
    woop,
  ])
);

const getFreeTextUrlKey = (urlKey: string) => `${urlKey}_freeText`;

type CategoryGroup = 'woop' | 'reporting';

const CATEGORY_GROUP: Record<WorkloadCategory, CategoryGroup | undefined> = {
  [WORKLOAD_CATEGORIES.AUTOSCALING]: 'woop',
  [WORKLOAD_CATEGORIES.SAVINGS]: 'woop',
  [WORKLOAD_CATEGORIES.COMPUTE]: 'reporting',
  [WORKLOAD_CATEGORIES.NETWORK]: 'reporting',
  [WORKLOAD_CATEGORIES.RELIABILITY]: 'reporting',
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: 'reporting',
  [WORKLOAD_CATEGORIES.DEFAULT]: undefined,
};

const CATEGORY_SEARCH_URL_KEYS: Partial<Record<WorkloadCategory, string>> = {
  [WORKLOAD_CATEGORIES.AUTOSCALING]: inventoryWorkloadsTableKeys.AUTOSCALING,
  [WORKLOAD_CATEGORIES.SAVINGS]: inventoryWorkloadsTableKeys.SAVINGS,
  [WORKLOAD_CATEGORIES.COMPUTE]: inventoryWorkloadsTableKeys.COMPUTE,
  [WORKLOAD_CATEGORIES.NETWORK]: inventoryWorkloadsTableKeys.NETWORK,
  [WORKLOAD_CATEGORIES.RELIABILITY]: inventoryWorkloadsTableKeys.RELIABILITY,
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]:
    inventoryWorkloadsTableKeys.GPU_UTILIZATION,
};

const decodeSearchState = (
  urlKey: string,
  searchParams: URLSearchParams
): Record<string, unknown> => {
  const raw = searchParams.get(urlKey);

  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const pickSharedFilters = (
  state: Record<string, unknown>,
  keys: readonly string[]
) =>
  Object.fromEntries(
    keys
      .filter((key) => state[key] !== undefined && state[key] !== null)
      .map((key) => [key, state[key]])
  );

const carryCrossGroup = (
  fromState: Record<string, unknown>,
  fromGroup: CategoryGroup
): Record<string, unknown> => {
  const renames =
    fromGroup === 'woop' ? WOOP_TO_REPORTING_KEYS : REPORTING_TO_WOOP_KEYS;
  return Object.fromEntries(
    Object.entries(renames)
      .filter(([fromKey]) => fromState[fromKey] != null)
      .map(([fromKey, toKey]) => [toKey, fromState[fromKey]])
  );
};

export const carryInventoryListFiltersAcrossCategories = (
  fromCategory: WorkloadCategory | undefined,
  toCategory: WorkloadCategory,
  searchParams: URLSearchParams
): URLSearchParams => {
  if (!fromCategory || fromCategory === toCategory) {
    return searchParams;
  }

  const fromUrlKey = CATEGORY_SEARCH_URL_KEYS[fromCategory];
  const toUrlKey = CATEGORY_SEARCH_URL_KEYS[toCategory];

  if (!fromUrlKey || !toUrlKey || fromUrlKey === toUrlKey) {
    return searchParams;
  }

  const fromState = decodeSearchState(fromUrlKey, searchParams);
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.delete(fromUrlKey);
  nextParams.delete(getFreeTextUrlKey(fromUrlKey));

  const fromGroup = CATEGORY_GROUP[fromCategory];
  const toGroup = CATEGORY_GROUP[toCategory];

  // Cross-group: only namespace, name and type carry; free text and the
  // other keys are group-only.
  if (fromGroup !== toGroup) {
    const carried =
      fromGroup && toGroup ? carryCrossGroup(fromState, fromGroup) : {};
    if (Object.keys(carried).length) {
      const toState = {
        ...decodeSearchState(toUrlKey, searchParams),
        ...carried,
      };
      nextParams.set(toUrlKey, encodeURIComponent(JSON.stringify(toState)));
    }
    return nextParams;
  }

  const sharedKeys =
    toGroup === 'woop' ? WOOP_SHARED_FILTER_KEYS : REPORTING_SHARED_FILTER_KEYS;
  const sharedFilters = pickSharedFilters(fromState, sharedKeys);

  // Both WOOP providers enable free text; carry it across by renaming the URL
  // param key. Reporting views set disableFreeText, so no free text to carry.
  if (toGroup === 'woop') {
    const fromFreeText = searchParams.get(getFreeTextUrlKey(fromUrlKey));
    if (fromFreeText) {
      nextParams.set(getFreeTextUrlKey(toUrlKey), fromFreeText);
    }
  }

  if (!Object.keys(sharedFilters).length) {
    return nextParams;
  }

  const toState = {
    ...decodeSearchState(toUrlKey, searchParams),
    ...sharedFilters,
  };

  nextParams.set(toUrlKey, encodeURIComponent(JSON.stringify(toState)));

  return nextParams;
};
